'use strict';

/**
 * PromptEmbed -- a reusable inline card for one attached prompt.
 *
 * It renders a prompt's title + badges, an expand/collapse body (the pinned version's text), a Copy
 * prompt text button, a Copy MCP run command button (when addressable), and -- when editable -- an
 * in-place Edit mode (a textarea + Save) that revises the text and re-pins the attachment so the MCP
 * run uses the new text, no trip to the Prompt Library. Host code can pass extra Actions (e.g. Record
 * run / Detach) rendered as header buttons with an OnAction callback.
 *
 * It replaces the old read-only "Read" modal in PictView-PromptAttachments and is meant to be mounted
 * in several places (the work item panel, the feedback page, ...). Event-delegation based (no inline
 * onclick, no global handles), so any number of instances coexist. Themed with the --cnx-* box vars
 * (with fallbacks), so it reads inside the graphite connections box and standalone.
 *
 *   let tmpEmbed = new PromptEmbed(pict);
 *   tmpEmbed.configure(pEntry, { RunCommand, Editable: true, Actions: [{ Key:'run', Label:'Record run' }], OnAction: (k)=>..., OnRevised: (body)=>... });
 *   tmpEmbed.mount(hostEl);
 */

const _STYLE_ID = 'pe-embed-styles';

function _esc(pValue)
{
	return String(pValue == null ? '' : pValue).replace(/[&<>"]/g, (pCh) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[pCh]));
}

const _CSS =
	'.pe { display: block; border: 1px solid var(--cnx-cellb, rgba(150,166,188,0.3)); border-radius: 9px; padding: 9px 11px; margin-bottom: 8px; background: rgba(8,14,24,0.14); }'
	+ '.pe-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }'
	+ '.pe-title { font-weight: 600; font-size: 13px; color: var(--cnx-txt, #eef2f7); cursor: pointer; }'
	+ '.pe-badge { font-family: ui-monospace, Menlo, monospace; font-size: 10px; letter-spacing: 1px; text-transform: uppercase; padding: 1px 7px; border-radius: 6px; border: 1px solid var(--cnx-cellb, rgba(150,166,188,0.3)); color: var(--cnx-lbl, #9aabc0); white-space: nowrap; }'
	+ '.pe-spacer { flex: 1; }'
	+ '.pe-btn { border: 1px solid var(--cnx-cellb, rgba(150,166,188,0.3)); background: rgba(8,14,24,0.4); color: var(--cnx-txt, #eef2f7); border-radius: 7px; padding: 3px 11px; font-size: 12px; cursor: pointer; white-space: nowrap; }'
	+ '.pe-btn:hover { border-color: #f0c44a; color: #f0c44a; }'
	+ '.pe-btn.pe-danger:hover { border-color: #e2725b; color: #e2725b; }'
	+ '.pe-run { display: flex; align-items: center; gap: 9px; margin: 9px 0 0; padding: 6px 12px; background: rgba(8,14,24,0.22); border: 1px dashed var(--cnx-cellb, rgba(150,166,188,0.3)); border-radius: 8px; }'
	+ '.pe-run-text { flex: 1; font-family: ui-monospace, Menlo, monospace; font-size: 11.5px; color: var(--cnx-txt, #eef2f7); white-space: pre-wrap; word-break: break-word; }'
	+ '.pe-body { margin: 9px 0 0; }'
	+ '.pe-pre { max-height: 52vh; overflow: auto; white-space: pre-wrap; word-break: break-word; font-family: ui-monospace, Menlo, monospace; font-size: 12px; color: var(--cnx-txt, #eef2f7); background: rgba(8,14,24,0.3); border: 1px solid var(--cnx-cellb, rgba(150,166,188,0.3)); border-radius: 7px; padding: 9px 11px; margin: 0; }'
	+ '.pe-edit-ta { width: 100%; box-sizing: border-box; min-height: 40vh; resize: vertical; font-family: ui-monospace, Menlo, monospace; font-size: 12px; color: var(--cnx-txt, #eef2f7); background: rgba(8,14,24,0.3); border: 1px solid var(--cnx-cellb, rgba(150,166,188,0.3)); border-radius: 7px; padding: 9px 11px; }'
	+ '.pe-edit-actions { display: flex; gap: 8px; margin-top: 8px; }'
	+ '.pe-hint { font-size: 11px; color: var(--cnx-lbl, #9aabc0); margin-top: 6px; }';

function _injectCSS()
{
	if (typeof document === 'undefined') { return; }
	if (document.getElementById(_STYLE_ID)) { return; }
	let tmpStyle = document.createElement('style');
	tmpStyle.id = _STYLE_ID;
	tmpStyle.textContent = _CSS;
	document.head.appendChild(tmpStyle);
}

class PromptEmbed
{
	constructor(pPict)
	{
		this.pict = pPict;
		this._entry = null;         // { Prompt, Version, Link }
		this._runCommand = '';
		this._editable = false;
		this._expanded = false;
		this._actions = [];         // [{ Key, Label, Danger }]
		this._onAction = function () {};
		this._onRevised = function () {};
		this._host = null;
		this._editing = false;
	}

	// pEntry: a { Prompt, Version, Link } row (the PromptLinks/For shape). Options:
	//   RunCommand (string), Editable (bool), Expanded (bool), Actions ([{Key,Label,Danger}]),
	//   OnAction (fn(key)), OnRevised (fn(body)).
	configure(pEntry, pOptions)
	{
		pOptions = pOptions || {};
		this._entry = pEntry || null;
		this._runCommand = pOptions.RunCommand || '';
		if (pOptions.Editable != null) { this._editable = !!pOptions.Editable; }
		if (pOptions.Expanded != null) { this._expanded = !!pOptions.Expanded; }
		this._actions = Array.isArray(pOptions.Actions) ? pOptions.Actions : [];
		if (typeof pOptions.OnAction === 'function') { this._onAction = pOptions.OnAction; }
		if (typeof pOptions.OnRevised === 'function') { this._onRevised = pOptions.OnRevised; }
		return this;
	}

	mount(pHostElement)
	{
		if (!pHostElement) { return this; }
		_injectCSS();
		this._host = pHostElement;
		pHostElement.classList.add('pe');
		pHostElement.addEventListener('click', (pEvent) => this._onClick(pEvent));
		this._render();
		return this;
	}

	_source() { return (this._entry && this._entry.Version && this._entry.Version.Source) || ''; }

	_onClick(pEvent)
	{
		let tmpBtn = pEvent.target.closest('[data-pe]');
		if (!tmpBtn || !this._host.contains(tmpBtn)) { return; }
		let tmpAction = tmpBtn.getAttribute('data-pe');
		if (tmpAction === 'toggle') { this._editing = false; this._expanded = !this._expanded; this._render(); }
		else if (tmpAction === 'copy') { this._copy(this._source(), 'Prompt text copied.'); }
		else if (tmpAction === 'copyrun') { this._copy(this._runCommand, 'Run command copied.'); }
		else if (tmpAction === 'edit') { this._editing = true; this._expanded = true; this._render(); }
		else if (tmpAction === 'cancel') { this._editing = false; this._render(); }
		else if (tmpAction === 'save') { this._save(); }
		else if (tmpAction === 'act') { this._onAction(tmpBtn.getAttribute('data-key')); }
	}

	_copy(pText, pOkMessage)
	{
		if (!pText) { return; }
		if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText)
		{
			navigator.clipboard.writeText(pText).then(() => this._toast(pOkMessage, 'success')).catch(() => this._toast('Copy failed.', 'error'));
		}
	}

	_save()
	{
		let tmpArea = this._host.querySelector('.pe-edit-ta');
		if (!tmpArea) { return; }
		let tmpSource = tmpArea.value || '';
		if (!tmpSource.trim()) { return this._toast('The prompt text cannot be empty.', 'error'); }
		let tmpIDLink = this._entry && this._entry.Link && this._entry.Link.IDPromptLink;
		let tmpAPI = this.pict.providers.PromptAPI;
		if (!tmpIDLink || !tmpAPI || typeof tmpAPI.revise !== 'function') { return this._toast('Cannot save here.', 'error'); }
		tmpAPI.revise(tmpIDLink, tmpSource).then((pBody) =>
		{
			// Reflect the new pinned version locally so the card + copy use the revised text.
			if (this._entry.Version)
			{
				this._entry.Version.Source = tmpSource;
				if (pBody && pBody.Version) { this._entry.Version.Sequence = pBody.Version.Sequence; this._entry.Version.IDPromptVersion = pBody.Version.IDPromptVersion; }
			}
			this._editing = false;
			this._render();
			this._toast('Prompt revised; the run now uses the new text.', 'success');
			this._onRevised(pBody);
		}).catch((pError) => this._toast((pError && pError.message) || 'Could not save the revision.', 'error'));
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: pType }); }
	}

	_render()
	{
		if (!this._host) { return; }
		let tmpEntry = this._entry || {};
		let tmpPrompt = tmpEntry.Prompt || {};
		let tmpVersion = tmpEntry.Version || null;
		let tmpLink = tmpEntry.Link || {};

		let tmpHead = '<div class="pe-head">'
			+ '<span class="pe-title" data-pe="toggle">' + _esc(tmpPrompt.Title || ('Prompt ' + tmpLink.IDPrompt)) + '</span>'
			+ '<span class="pe-badge">#' + _esc(tmpPrompt.PromptNumber || tmpLink.IDPrompt) + '</span>'
			+ (tmpPrompt.TypeKey ? '<span class="pe-badge">' + _esc(tmpPrompt.TypeKey) + '</span>' : '')
			+ (tmpVersion ? '<span class="pe-badge">v' + _esc(tmpVersion.Sequence) + ' pinned</span>' : '')
			+ '<span class="pe-spacer"></span>'
			+ (tmpVersion ? '<button class="pe-btn" data-pe="toggle">' + (this._expanded ? 'Hide' : 'Read') + '</button>' : '')
			+ (tmpVersion ? '<button class="pe-btn" data-pe="copy">Copy</button>' : '')
			+ (this._runCommand ? '<button class="pe-btn" data-pe="copyrun">Copy MCP command</button>' : '')
			+ (this._editable && tmpVersion ? '<button class="pe-btn" data-pe="edit">Edit</button>' : '')
			+ this._actions.map((pAction) => '<button class="pe-btn' + (pAction.Danger ? ' pe-danger' : '') + '" data-pe="act" data-key="' + _esc(pAction.Key) + '">' + _esc(pAction.Label) + '</button>').join('')
			+ '</div>';

		let tmpRun = this._runCommand
			? '<div class="pe-run"><code class="pe-run-text">' + _esc(this._runCommand) + '</code><button class="pe-btn" data-pe="copyrun">Copy</button></div>'
			: '';

		let tmpBody = '';
		if (this._expanded && tmpVersion)
		{
			if (this._editing)
			{
				tmpBody = '<div class="pe-body"><textarea class="pe-edit-ta">' + _esc(this._source()) + '</textarea>'
					+ '<div class="pe-edit-actions"><button class="pe-btn" data-pe="save">Save</button><button class="pe-btn" data-pe="cancel">Cancel</button></div>'
					+ '<div class="pe-hint">Saving captures a new pinned version, so the MCP run uses this text.</div></div>';
			}
			else
			{
				tmpBody = '<div class="pe-body"><pre class="pe-pre">' + _esc(this._source()) + '</pre></div>';
			}
		}

		this._host.innerHTML = tmpHead + tmpRun + tmpBody;
	}
}

module.exports = PromptEmbed;
